import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Lead } from '../Inflow';
import LeadCard from './LeadCard';
import { Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

// The styled components with enhanced visual feedback
const ColumnContainer = styled(Paper)(({ theme }) => ({
  height: { xs: 'calc(100vh - 200px)', sm: 'calc(100vh - 300px)' },
  minHeight: { xs: '400px', sm: '500px' },
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[50],
  boxShadow: 'none',
  border: `1px solid ${theme.palette.divider}`,
}));

const ColumnHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTopLeftRadius: theme.shape.borderRadius,
  borderTopRightRadius: theme.shape.borderRadius,
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const ColumnContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  flexGrow: 1,
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.grey[100],
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.grey[300],
    borderRadius: '3px',
  },
}));

// Style for droppable areas when being dragged over
const getDroppableStyle = (isDraggingOver: boolean, status: string) => {
  // Define status-specific colors for drop zones
  const statusColors = {
    hot: 'rgba(244, 67, 54, 0.15)',
    cold: 'rgba(33, 150, 243, 0.15)',
    obsolete: 'rgba(158, 158, 158, 0.15)',
    win: 'rgba(76, 175, 80, 0.15)'
  };
  
  const color = statusColors[status as keyof typeof statusColors] || 'rgba(0, 0, 0, 0.05)';
  
  return {
    background: isDraggingOver ? color : 'transparent',
    borderRadius: '8px',
    transition: 'background-color 0.3s ease',
    minHeight: '100%',
    padding: '8px',
    border: isDraggingOver ? '2px dashed rgba(0, 0, 0, 0.2)' : '2px solid transparent',
    boxShadow: isDraggingOver ? 'inset 0 0 20px rgba(0, 0, 0, 0.05)' : 'none',
  };
};

// Simplified draggable style function
const getDraggableStyle = (isDragging: boolean, draggableStyle: any) => ({
  userSelect: 'none',
  padding: 0,
  margin: '0 0 8px 0',
  // Apply styles from drag-and-drop library
  ...draggableStyle,
});

// Visual drag handle component
const DragHandle = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '-14px',
  transform: 'translateY(-50%)',
  color: theme.palette.grey[400],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'grab',
  zIndex: 10,
  '&:hover': {
    color: theme.palette.primary.main,
  }
}));

// Instructions component for first-time users
const DragInstructions = () => (
  <Box 
    sx={{ 
      mb: 2, 
      p: 2, 
      bgcolor: 'rgba(255, 193, 7, 0.1)', 
      borderRadius: 1,
      border: '1px dashed rgba(255, 193, 7, 0.5)',
      display: 'flex',
      alignItems: 'center'
    }}
  >
    <DragIndicatorIcon sx={{ mr: 1, color: '#ffc107' }} />
    <Typography variant="body2">
      Click and hold on any card, then drag it to another column to change its status
    </Typography>
  </Box>
);

interface LeadsBoardProps {
  leads: Lead[];
  onEdit: (lead: Lead) => void;
  onDelete: (id: string) => void;
  onMove: (id: string, status: 'hot' | 'cold' | 'obsolete' | 'win') => void;
}

const LeadsBoard: React.FC<LeadsBoardProps> = ({ leads, onEdit, onDelete, onMove }) => {
  const hotLeads = leads.filter((lead) => lead.status === 'hot');
  const coldLeads = leads.filter((lead) => lead.status === 'cold');
  const obsoleteLeads = leads.filter((lead) => lead.status === 'obsolete');
  const winLeads = leads.filter((lead) => lead.status === 'win');

  // Add a state to track collapsed cards by ID
  const [collapsedCards, setCollapsedCards] = useState<Record<string, boolean>>({});

  // Initialize all cards as collapsed when leads change
  useEffect(() => {
    const initialCollapsedState: Record<string, boolean> = {};
    leads.forEach(lead => {
      initialCollapsedState[lead.id] = true; // Set all cards to collapsed by default
    });
    setCollapsedCards(initialCollapsedState);
  }, []);

  // Function to toggle card collapse state
  const toggleCardCollapse = (leadId: string) => {
    setCollapsedCards(prev => ({
      ...prev,
      [leadId]: !prev[leadId]
    }));
  };

  const handleDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;

    // If there's no destination or the item is dropped in the same place
    if (!destination || 
        (destination.droppableId === source.droppableId && 
         destination.index === source.index)) {
      return;
    }

    // Get the new status from the destination droppableId
    const newStatus = destination.droppableId as 'hot' | 'cold' | 'obsolete' | 'win';
    
    // Call the onMove function to update the lead status
    onMove(draggableId, newStatus);
  };

  return (
    <>
      <DragInstructions />
      <Box sx={{ 
        display: { xs: 'flex', sm: 'block' },
        overflowX: { xs: 'auto', sm: 'visible' },
        gap: { xs: 2, sm: 0 },
        pb: { xs: 2, sm: 0 },
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': {
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px',
        },
      }}>
        <Grid container spacing={2} sx={{ 
          flexWrap: { xs: 'nowrap', sm: 'wrap' },
          width: { xs: 'max-content', sm: '100%' },
        }}>
          <Grid item xs={12} sm={6} md={3} sx={{ 
            minWidth: { xs: '280px', sm: 'auto' },
            maxWidth: { xs: '280px', sm: 'none' },
          }}>
          <ColumnContainer>
            <ColumnHeader sx={{ backgroundColor: 'rgba(244, 67, 54, 0.08)' }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Hot Leads
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {hotLeads.length}
              </Typography>
            </ColumnHeader>
            <Droppable droppableId="hot">
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={getDroppableStyle(snapshot.isDraggingOver, 'hot')}
                >
                  {hotLeads.map((lead, index) => (
                    <Draggable key={lead.id} draggableId={lead.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={getDraggableStyle(snapshot.isDragging, provided.draggableProps.style)}
                        >
                          <LeadCard
                            lead={lead}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onMove={onMove}
                            isDragging={snapshot.isDragging}
                            isCollapsed={collapsedCards[lead.id] || false}
                            onToggleCollapse={() => toggleCardCollapse(lead.id)}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </ColumnContainer>
        </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ 
            minWidth: { xs: '280px', sm: 'auto' },
            maxWidth: { xs: '280px', sm: 'none' },
          }}>
            <ColumnContainer>
              <ColumnHeader sx={{ backgroundColor: 'rgba(33, 150, 243, 0.08)' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Cold Leads
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {coldLeads.length}
                </Typography>
              </ColumnHeader>
              <Droppable droppableId="cold">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={getDroppableStyle(snapshot.isDraggingOver, 'cold')}
                  >
                    {coldLeads.map((lead, index) => (
                      <Draggable key={lead.id} draggableId={lead.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={getDraggableStyle(snapshot.isDragging, provided.draggableProps.style)}
                          >
                            <LeadCard
                              lead={lead}
                              onEdit={onEdit}
                              onDelete={onDelete}
                              onMove={onMove}
                              isDragging={snapshot.isDragging}
                              isCollapsed={collapsedCards[lead.id] || false}
                              onToggleCollapse={() => toggleCardCollapse(lead.id)}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </ColumnContainer>
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ 
            minWidth: { xs: '280px', sm: 'auto' },
            maxWidth: { xs: '280px', sm: 'none' },
          }}>
            <ColumnContainer>
              <ColumnHeader sx={{ backgroundColor: 'rgba(158, 158, 158, 0.08)' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Obsolete Leads
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {obsoleteLeads.length}
                </Typography>
              </ColumnHeader>
              <Droppable droppableId="obsolete">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={getDroppableStyle(snapshot.isDraggingOver, 'obsolete')}
                  >
                    {obsoleteLeads.map((lead, index) => (
                      <Draggable key={lead.id} draggableId={lead.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={getDraggableStyle(snapshot.isDragging, provided.draggableProps.style)}
                          >
                            <LeadCard
                              lead={lead}
                              onEdit={onEdit}
                              onDelete={onDelete}
                              onMove={onMove}
                              isDragging={snapshot.isDragging}
                              isCollapsed={collapsedCards[lead.id] || false}
                              onToggleCollapse={() => toggleCardCollapse(lead.id)}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </ColumnContainer>
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ 
            minWidth: { xs: '280px', sm: 'auto' },
            maxWidth: { xs: '280px', sm: 'none' },
          }}>
          <ColumnContainer>
            <ColumnHeader sx={{ backgroundColor: 'rgba(76, 175, 80, 0.08)' }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Wins
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {winLeads.length}
              </Typography>
            </ColumnHeader>
            <Droppable droppableId="win">
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={getDroppableStyle(snapshot.isDraggingOver, 'win')}
                >
                  {winLeads.map((lead, index) => (
                    <Draggable key={lead.id} draggableId={lead.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={getDraggableStyle(snapshot.isDragging, provided.draggableProps.style)}
                        >
                          <LeadCard
                            lead={lead}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onMove={onMove}
                            isDragging={snapshot.isDragging}
                            isCollapsed={collapsedCards[lead.id] || false}
                            onToggleCollapse={() => toggleCardCollapse(lead.id)}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </ColumnContainer>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default LeadsBoard; 